<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router'
import ReadyMadeCompanyDataService from '@/services/ReadyMadeCompanyDataService'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

const router = useRouter()
const route = useRoute()
const loading = ref(false)

const companyId = computed(() => route.params.id as string | undefined)
const isEdit = computed(() => !!companyId.value)

const form = reactive({
  company_name: '',
  ico: '',
  headquarters: '',
  vat_payer: false as boolean | undefined,
  founding_date: '',
  price: 0,
  sale_price: undefined as number | undefined,
})

const errors = reactive({
  company_name: '',
  ico: '',
  headquarters: '',
  founding_date: '',
  price: '',
})

onMounted(async () => {
  if (isEdit.value) {
    await fetchCompany()
  }
})

async function fetchCompany() {
  loading.value = true
  try {
    const response = await ReadyMadeCompanyDataService.getById(Number(companyId.value))
    if (response.data?.success) {
      const data = response.data.data
      form.company_name = data.company_name
      form.ico = data.ico
      form.headquarters = data.headquarters
      form.vat_payer = data.vat_payer
      form.founding_date = data.founding_date ? data.founding_date.substring(0, 10) : ''
      form.price = data.price
      form.sale_price = data.sale_price ?? null
    }
  } catch (error) {
    toast.error('Chyba pri načítavaní firmy')
    router.push({ name: 'Ready made firmy' })
  } finally {
    loading.value = false
  }
}

function validate(): boolean {
  errors.company_name = ''
  errors.ico = ''
  errors.headquarters = ''
  errors.founding_date = ''
  errors.price = ''

  let valid = true

  if (!form.company_name.trim()) {
    errors.company_name = 'Názov firmy je povinný'
    valid = false
  }
  if (!form.ico.trim()) {
    errors.ico = 'IČO je povinné'
    valid = false
  }
  if (!form.headquarters.trim()) {
    errors.headquarters = 'Sídlo je povinné'
    valid = false
  }
  if (!form.founding_date) {
    errors.founding_date = 'Dátum vzniku je povinný'
    valid = false
  }
  if (form.price == null || form.price < 0) {
    errors.price = 'Cena je povinná a musí byť kladná'
    valid = false
  }

  return valid
}

async function save() {
  if (!validate()) return

  loading.value = true
  try {
    const payload = {
      company_name: form.company_name,
      ico: form.ico,
      headquarters: form.headquarters,
      vat_payer: Boolean(form.vat_payer),
      founding_date: form.founding_date,
      price: form.price,
      sale_price: form.sale_price || null,
    }

    if (isEdit.value) {
      await ReadyMadeCompanyDataService.update(Number(companyId.value), payload)
      toast.success('Firma bola úspešne upravená')
    } else {
      await ReadyMadeCompanyDataService.create(payload)
      toast.success('Firma bola úspešne pridaná')
    }

    router.push({ name: 'Ready made firmy' })
  } catch (error: any) {
    const msg = error?.response?.data?.message || 'Nastala chyba pri ukladaní'
    toast.error(msg)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <main class="flex-1 overflow-y-auto focus:outline-none">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <PageBreadcrumb :page-title="isEdit ? 'Upraviť ready made firmu' : 'Pridať ready made firmu'" />
      </div>
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <div class="py-4">
          <form @submit.prevent="save" class="space-y-8 divide-y divide-gray-200 dark:divide-gray-800">
            <div class="space-y-6">
              <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-4">
                  <label for="company_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Názov firmy</label>
                  <div class="mt-1">
                    <FormKit
                      type="text"
                      id="company_name"
                      name="company_name"
                      v-model="form.company_name"
                      :classes="{
                        outer: 'mb-0',
                        input: 'shadow-xs focus:ring-brand-500 focus:border-brand-500 block w-full sm:text-sm border-gray-300 rounded-md dark:border-gray-700'
                      }"
                      placeholder="Napríklad: ABC s.r.o."
                    />
                    <p v-if="errors.company_name" class="mt-2 text-sm text-red-600">{{ errors.company_name }}</p>
                  </div>
                </div>
                <div class="sm:col-span-4">
                  <label for="ico" class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČO</label>
                  <div class="mt-1">
                    <FormKit
                      type="text"
                      id="ico"
                      name="ico"
                      v-model="form.ico"
                      :classes="{
                        outer: 'mb-0',
                        input: 'shadow-xs focus:ring-brand-500 focus:border-brand-500 block w-full sm:text-sm border-gray-300 rounded-md dark:border-gray-700'
                      }"
                      placeholder="12345678"
                    />
                    <p v-if="errors.ico" class="mt-2 text-sm text-red-600">{{ errors.ico }}</p>
                  </div>
                </div>
                <div class="sm:col-span-4">
                  <label for="headquarters" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Sídlo</label>
                  <div class="mt-1">
                    <FormKit
                      type="text"
                      id="headquarters"
                      name="headquarters"
                      v-model="form.headquarters"
                      :classes="{
                        outer: 'mb-0',
                        input: 'shadow-xs focus:ring-brand-500 focus:border-brand-500 block w-full sm:text-sm border-gray-300 rounded-md dark:border-gray-700'
                      }"
                      placeholder="Bratislava, Staré Mesto 1"
                    />
                    <p v-if="errors.headquarters" class="mt-2 text-sm text-red-600">{{ errors.headquarters }}</p>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label for="vat_payer" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Plátca DPH</label>
                  <div class="mt-1 flex items-center">
                    <input type="checkbox" id="vat_payer" name="vat_payer" v-model="form.vat_payer" class="rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label for="founding_date" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Dátum vzniku</label>
                  <div class="mt-1">
                    <FormKit type="date" id="founding_date" name="founding_date" v-model="form.founding_date" />
                    <p v-if="errors.founding_date" class="mt-2 text-sm text-red-600">{{ errors.founding_date }}</p>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label for="price" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Cena</label>
                  <div class="mt-1">
                    <FormKit type="text" number id="price" name="price" v-model="form.price" step="0.01" />
                    <p v-if="errors.price" class="mt-2 text-sm text-red-600">{{ errors.price }}</p>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label for="sale_price" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Predajná cena</label>
                  <div class="mt-1">
                    <FormKit type="text" number id="sale_price" name="sale_price" v-model="form.sale_price" step="0.01" />
                  </div>
                </div>
              </div>
              <div class="mt-8 flex justify-end">
                <button type="button" class="px-4 py-2 bg-gray-300 rounded-sm dark:bg-gray-700 text-sm" @click="router.push({ name: 'Ready made firmy' })">Späť</button>
                <button type="submit" class="ml-3 px-4 py-2 bg-brand-600 text-white rounded-sm text-sm" :disabled="loading">
                  {{ loading ? 'Ukladám...' : 'Uložiť' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
/* Add any component-specific styles here */
</style>
